import { useMemo } from 'react';
import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import { di } from 'react-magnetic-di';
import { useAdjustedFields } from '../../../common/fake-uim/store';
import type { AdjustedField, AdjustedFieldValue } from '../../../common/fake-uim/store/types';
import type { FieldState } from 'state/persisted/request-create/types';

// TODO: Replace with real useAdjustedFields and AdjustedField once uim is ready on platfrom.
const pickPropertiesToOverride = (field: AdjustedField): Partial<FieldState> => {
    const overrideProperties = {
        fieldName: 'label',
        description: 'description',
        isReadOnly: 'disabled',
        isRequired: 'required',
        isVisible: 'displayed',
        'value.details': 'valueOverride',
        // TODO: decide if use options overrides or add the filter prop to implement overrides inside each component.
        // options is easier, but filter is more future proof https://ecosystem-platform.atlassian.net/browse/UIM-2336
        // options: 'options',
        // filterOptionsById: 'filterOptionsById',
    } satisfies Partial<Record<keyof AdjustedField | `value.${keyof AdjustedFieldValue}`, keyof FieldState>>;

    return Object.entries(overrideProperties).reduce((acc: Record<string, unknown>, [uimProperty, jsmProperty]) => {
        if (has(field, uimProperty)) {
            acc[jsmProperty] = get(field, uimProperty);
        }

        return acc;
    }, {} as Partial<FieldState>);
};

export const useFieldsOverrides = ({ fields }: { fields: FieldState[] }) => {
    di(useAdjustedFields);
    const overrides = useAdjustedFields();

    return useMemo(() => {
        if (!overrides) {
            return { fields };
        }

        const overridenFields = fields.map((field: FieldState) => {
            const fieldOverrides = overrides[field.id];
            if (!fieldOverrides) {
                return field;
            }

            const supportedOverrides = pickPropertiesToOverride(fieldOverrides);

            if (isEmpty(supportedOverrides)) {
                return field;
            }

            return {
                ...field,
                ...supportedOverrides,
            } as FieldState;
        });

        return { fields: overridenFields };
    }, [overrides, fields]);
};
